import React from "react";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { AlertCircleIcon } from "lucide-react-native";
import { useForm, useFormContext } from "react-hook-form";

import { VnAlert, VnAlertTitle } from "@/components/VnAlert";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCurrencyInput } from "@/components/VnCurrencyInput";
import { VnForm } from "@/components/VnForm";
import { VnRadioGroupInput } from "@/components/VnRadioGroupInput/VnRadioGroupInput";
import { VnText } from "@/components/VnText/VnText";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import {
  EXPENSE_NUMERIC_FIELDS,
  ExpenseTypeEnum,
  getExpenseTypeAlertDescriptionI18nKey,
  getExpenseTypeI18nKey,
  householdMemberExpensesFormSchema,
  householdMemberMedicalExpenses3MonthsHelpNeededStatus,
  HouseholdMemberType,
  medicalExpensesFormSchema,
  MedicalExpensesFormType,
} from "@/domain/householdMember";
import { transformFormValues } from "@/utils/householdMember";

import { ScreenLayout } from "./ScreenLayout";

const MedicalExpenseForm = ({
  member,
  onClose,
  isEditMode,
}: {
  member: HouseholdMemberType;
  onClose: () => void;
  isEditMode: boolean;
}) => {
  const form = useForm<MedicalExpensesFormType>({
    defaultValues: {
      medicalExpenses3MonthsHelpNeededStatus:
        member.medicalExpenses3MonthsHelpNeededStatus ?? null,
    },
    resolver: standardSchemaResolver(medicalExpensesFormSchema),
  });

  return (
    <VnForm form={form} isInputOnlyForm className="flex-1 flex-col gap-6">
      <VnText
        variant="lgMedium"
        i18nKey="native:applicationExpensesScreen.memberDialog.medicalExpenses3MonthsHelpNeededStatus"
      />
      <VnRadioGroupInput
        name="medicalExpenses3MonthsHelpNeededStatus"
        control={form.control}
        options={householdMemberMedicalExpenses3MonthsHelpNeededStatus}
        className="gap-6"
        radioGroupClassName="gap-6"
      />
      <FormFooter
        memberId={member.id}
        onClose={onClose}
        isEditMode={isEditMode}
      />
    </VnForm>
  );
};

const simpleExpenseFormFieldMap: Record<
  ExpenseTypeEnum,
  SimpleExpenseFormPropsType["fieldName"] | null
> = {
  [ExpenseTypeEnum.NONE]: null,
  [ExpenseTypeEnum.ALIMONY_PAID]: "alimonyPaidMonthlyExpense",
  [ExpenseTypeEnum.CHILDCARE]: "childcareMonthlyExpense",
  [ExpenseTypeEnum.IRA_CONTRIBUTIONS]: "iraContributionsMonthlyExpense",
  [ExpenseTypeEnum.MEDICAL_EXPENSES]: null,
  [ExpenseTypeEnum.MOVING_EXPENSES]: "movingMonthlyExpense",
  [ExpenseTypeEnum.OTHER]: "otherMonthlyExpense",
  [ExpenseTypeEnum.STUDENT_LOANS]: "studentLoansMonthlyExpense",
  [ExpenseTypeEnum.TUITION_FEES]: "tuitionFeesMonthlyExpense",
};

type SimpleExpenseFormPropsType = {
  member: HouseholdMemberType;
  onClose: () => void;
  fieldName:
    | "alimonyPaidMonthlyExpense"
    | "childcareMonthlyExpense"
    | "iraContributionsMonthlyExpense"
    | "movingMonthlyExpense"
    | "otherMonthlyExpense"
    | "studentLoansMonthlyExpense"
    | "tuitionFeesMonthlyExpense";
  isEditMode: boolean;
};

const SimpleExpenseForm = ({
  member,
  onClose,
  fieldName,
  isEditMode,
}: SimpleExpenseFormPropsType) => {
  type FormType = Record<typeof fieldName, string | null>;
  const formSchema = householdMemberExpensesFormSchema.pick({
    [fieldName]: true,
  } as Record<typeof fieldName, true>);

  const form = useForm<FormType>({
    defaultValues: {
      [fieldName]: member[fieldName] ? String(member[fieldName]) : null,
    },
    resolver: standardSchemaResolver(formSchema),
  });

  return (
    <VnForm form={form} isInputOnlyForm className="flex-1 flex-col gap-6">
      <VnCurrencyInput
        isRequired={false}
        control={form.control}
        name={fieldName}
        labelI18nKey="native:applicationFlow.amountPerMonth"
      />
      <FormFooter
        memberId={member.id}
        onClose={onClose}
        isEditMode={isEditMode}
      />
    </VnForm>
  );
};

const FormFooter = ({
  memberId,
  onClose,
  isEditMode,
}: {
  memberId: string;
  onClose: () => void;
  isEditMode: boolean;
}) => {
  const form = useFormContext();

  const {
    mutate: updateHouseholdMember,
    isPending: isUpdatingHouseholdMember,
  } = useUpdateHouseholdMemberMutation();
  const handleClose = () => {
    onClose();
    form.reset();
  };

  const handleDelete = (formValues: Record<string, unknown>) => {
    const transformedValues = { ...formValues };

    for (const field in transformedValues) {
      transformedValues[field] = null;
    }

    updateHouseholdMember(
      {
        id: memberId,
        householdMemberData: transformedValues,
      },
      {
        onSuccess: handleClose,
      },
    );
  };

  const handleSave = (formValues: Record<string, unknown>) => {
    const transformedValues = transformFormValues(
      formValues,
      EXPENSE_NUMERIC_FIELDS,
    );
    updateHouseholdMember(
      {
        id: memberId,
        householdMemberData: { ...transformedValues, hasExpense: true },
      },
      { onSuccess: handleClose },
    );
  };

  return (
    <VnBox className="mt-auto flex shrink-0 flex-row justify-between gap-2 px-0">
      {isEditMode && (
        <VnButton
          variant="destructive"
          size="lg"
          i18nKey="common:delete"
          onPress={() => {
            void form.handleSubmit(handleDelete)();
          }}
          className="flex-1 sm:flex-none"
        />
      )}
      <VnButton
        size="lg"
        variant="outline"
        i18nKey="common:cancel"
        onPress={handleClose}
        className="flex-1 sm:ml-auto sm:flex-none"
      />
      <VnButton
        size="lg"
        i18nKey="common:save"
        disabled={!form.formState.isDirty}
        onPress={() => {
          void form.handleSubmit(handleSave)();
        }}
        className="flex-1 sm:flex-none"
        isLoading={isUpdatingHouseholdMember}
      />
    </VnBox>
  );
};

type ExpensesFormPropsType = {
  member: HouseholdMemberType;
  selectedExpenseType: ExpenseTypeEnum;
  onClose: () => void;
  isEditMode: boolean;
};

export const ExpensesForm = ({
  member,
  selectedExpenseType,
  onClose,
  isEditMode,
}: ExpensesFormPropsType) => {
  const fieldName = simpleExpenseFormFieldMap[selectedExpenseType];

  return (
    <VnBox className="flex-1 flex-col">
      <VnText
        variant="lg"
        className="text-muted-foreground mb-2 shrink-0 text-center"
        i18nKey={getExpenseTypeI18nKey(selectedExpenseType)}
      />
      <ScreenLayout
        variant="FORM"
        backgroundClassName="bg-background"
        hasHorizontalPadding={false}>
        <VnBox className="flex-1 flex-col gap-6 px-4 py-1">
          <VnAlert
            icon={AlertCircleIcon}
            className="bg-secondary"
            iconClassName="text-muted-foreground size-5 mt-0.5">
            <VnAlertTitle
              className="font-primary text-muted-foreground text-base leading-6 tracking-normal"
              i18nKey={getExpenseTypeAlertDescriptionI18nKey(
                selectedExpenseType,
              )}
            />
          </VnAlert>
          {selectedExpenseType === ExpenseTypeEnum.MEDICAL_EXPENSES && (
            <MedicalExpenseForm
              member={member}
              onClose={onClose}
              isEditMode={isEditMode}
            />
          )}
          {fieldName && (
            <SimpleExpenseForm
              member={member}
              onClose={onClose}
              fieldName={fieldName}
              isEditMode={isEditMode}
            />
          )}
        </VnBox>
      </ScreenLayout>
    </VnBox>
  );
};
